/**
 * gestureBimanual.js — two-hand static gesture scorers (Phase 5).
 *
 * Each scorer receives `hands: [hand0, hand1]` (after VisionAgent's new
 * payload shape) and returns confidence in [0, 1]. The classifier picks the
 * highest-confidence scorer above BIMANUAL_CONFIDENCE.
 *
 * IMPORTANT — MediaPipe `handedness`:
 *   The "Left" / "Right" label is from the SIGNER'S point of view, NOT the
 *   camera's. With selfie-mirrored video, a hand labeled "Left" appears on
 *   the right side of the screen. None of these scorers depend on which
 *   physical hand is which; they are symmetric (book/more/house all look the
 *   same regardless of which hand is left or right).
 *
 * Thresholds exported via BIMANUAL_THRESHOLDS for live tuning.
 */

// ---------------------- helpers ----------------------
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const palmCenter = (lm) => ({
  x: (lm[0].x + lm[5].x + lm[9].x + lm[13].x + lm[17].x) / 5,
  y: (lm[0].y + lm[5].y + lm[9].y + lm[13].y + lm[17].y) / 5,
});

const palmLength = (lm) => dist(lm[0], lm[9]);

/** Returns a 0..1 "flatness" score: fraction of 4 non-thumb fingers extended. */
function flatScore(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const palm = palmCenter(lm);
  const tips = [8, 12, 16, 20];
  const mcps = [5, 9, 13, 17];
  let ext = 0;
  for (let i = 0; i < 4; i++) {
    if (dist(lm[tips[i]], palm) > dist(lm[mcps[i]], palm) * 1.3) ext++;
  }
  return ext / 4;
}

/** Returns a 0..1 "pinch" score: closer thumb tip ↔ index tip = higher. */
function pinchScore(lm) {
  const palmLen = palmLength(lm);
  if (palmLen < 0.001) return 0;
  const d = dist(lm[4], lm[8]) / palmLen;
  if (d >= 0.45) return 0;
  // Linear: 0 distance → 1.0, 0.45*palmLen → 0
  return 1 - (d / 0.45);
}

// ============================================================
// BIMANUAL_THRESHOLDS — tune these
// ============================================================
// THRESHOLD: BIMANUAL_CONFIDENCE — minimum score to confirm a bimanual sign.
//   Lower (0.65) = more permissive, higher (0.85) = strict.
const BIMANUAL_CONFIDENCE = 0.75;

// THRESHOLD: BOOK_WRIST_PROXIMITY_MAX — wrists must be within this distance
//   (normalized 0..1) for "Book" to count. Bajar si Book no detecta porque
//   las manos no están suficientemente cerca.
const BOOK_WRIST_PROXIMITY_MAX = 0.25;

// THRESHOLD: BOOK_HEIGHT_DIFF_MAX — wrists must be at roughly the same height
//   (|y0 - y1| < this). 0.10 ≈ 10 % of frame.
const BOOK_HEIGHT_DIFF_MAX = 0.10;

// THRESHOLD: MORE_PINCH_PROXIMITY_MAX — the two pinch centroids must be within
//   this distance. Larger = more permissive.
const MORE_PINCH_PROXIMITY_MAX = 0.12;

// THRESHOLD: HOUSE_FINGERTIP_MEET_MAX — middle fingertips (lm[12]) must be
//   within this distance for the "roof" peak.
const HOUSE_FINGERTIP_MEET_MAX = 0.10;

// THRESHOLD: HOUSE_WRIST_SPREAD_MIN — wrists must be at least this far apart
//   to form the pyramid base.
const HOUSE_WRIST_SPREAD_MIN = 0.18;

// THRESHOLD: HOUSE_WRIST_Y_MIN / HOUSE_TIP_Y_MAX — pyramid proportion:
//   wrists below midfield, tips above. y axis: 0 = top, 1 = bottom.
const HOUSE_WRIST_Y_MIN = 0.40;
const HOUSE_TIP_Y_MAX = 0.55;

// ============================================================
// Scorers
// ============================================================

/** Book — both flat palms close together at the same height. */
export function scoreBook(hands) {
  if (!hands || hands.length < 2) return 0;
  const [h0, h1] = hands;
  if (!h0?.landmarks || !h1?.landmarks) return 0;

  const flat0 = flatScore(h0.landmarks);
  const flat1 = flatScore(h1.landmarks);
  if (flat0 < 0.5 || flat1 < 0.5) return 0;

  const wristDist = dist(h0.landmarks[0], h1.landmarks[0]);
  if (wristDist > BOOK_WRIST_PROXIMITY_MAX) return 0;
  const proximity = 1 - wristDist / BOOK_WRIST_PROXIMITY_MAX;

  const heightDiff = Math.abs(h0.landmarks[0].y - h1.landmarks[0].y);
  if (heightDiff > BOOK_HEIGHT_DIFF_MAX) return 0;
  const heightAlign = 1 - heightDiff / BOOK_HEIGHT_DIFF_MAX;

  // Multiplicative — all four factors must be solid
  return Math.min(1, flat0 * flat1 * proximity * heightAlign);
}

/** More — both pinch handshapes with the two pinches kissing in the middle. */
export function scoreMore(hands) {
  if (!hands || hands.length < 2) return 0;
  const [h0, h1] = hands;
  if (!h0?.landmarks || !h1?.landmarks) return 0;

  const pinch0 = pinchScore(h0.landmarks);
  const pinch1 = pinchScore(h1.landmarks);
  if (pinch0 < 0.5 || pinch1 < 0.5) return 0;

  // Pinch centroid: midpoint between thumb tip (4) and index tip (8)
  const c0 = {
    x: (h0.landmarks[4].x + h0.landmarks[8].x) / 2,
    y: (h0.landmarks[4].y + h0.landmarks[8].y) / 2,
  };
  const c1 = {
    x: (h1.landmarks[4].x + h1.landmarks[8].x) / 2,
    y: (h1.landmarks[4].y + h1.landmarks[8].y) / 2,
  };
  const pinchProx = dist(c0, c1);
  if (pinchProx > MORE_PINCH_PROXIMITY_MAX) return 0;
  const proxScore = 1 - pinchProx / MORE_PINCH_PROXIMITY_MAX;

  return Math.min(1, pinch0 * 0.30 + pinch1 * 0.30 + proxScore * 0.40);
}

/** House — both flat hands at an angle, middle-fingertips meeting at top,
 *  wrists splayed below: a pyramid / roof. */
export function scoreHouse(hands) {
  if (!hands || hands.length < 2) return 0;
  const [h0, h1] = hands;
  if (!h0?.landmarks || !h1?.landmarks) return 0;

  const flat0 = flatScore(h0.landmarks);
  const flat1 = flatScore(h1.landmarks);
  if (flat0 < 0.5 || flat1 < 0.5) return 0;

  // Middle-fingertip meeting at peak
  const tipDist = dist(h0.landmarks[12], h1.landmarks[12]);
  if (tipDist > HOUSE_FINGERTIP_MEET_MAX) return 0;
  const tipScore = 1 - tipDist / HOUSE_FINGERTIP_MEET_MAX;

  // Wrists splayed
  const wristDist = dist(h0.landmarks[0], h1.landmarks[0]);
  if (wristDist < HOUSE_WRIST_SPREAD_MIN) return 0;
  // Cap at 1.5× threshold (saturate)
  const spreadScore = Math.min(wristDist / HOUSE_WRIST_SPREAD_MIN, 1.5) / 1.5;

  // Pyramid proportion: tips ABOVE wrists in frame
  const wristY = (h0.landmarks[0].y + h1.landmarks[0].y) / 2;
  const tipY = (h0.landmarks[12].y + h1.landmarks[12].y) / 2;
  if (wristY < HOUSE_WRIST_Y_MIN) return 0;
  if (tipY > HOUSE_TIP_Y_MAX) return 0;
  if (tipY > wristY) return 0; // tips must be HIGHER (smaller y) than wrists

  return Math.min(1, flat0 * 0.20 + flat1 * 0.20 + tipScore * 0.35 + spreadScore * 0.25);
}

// ============================================================
// Classifier
// ============================================================

const BIMANUAL_SCORERS = {
  book: scoreBook,
  more: scoreMore,
  house: scoreHouse,
};

/**
 * Pick the best bimanual sign for the current frame.
 * @param {Array<{landmarks: any[], handedness: string, score: number}>} hands
 * @param {Set<string>|null} allowedIds
 * @returns {{id, confidence}|null}
 */
export function classifyBimanual(hands, allowedIds = null) {
  if (!Array.isArray(hands) || hands.length < 2) return null;
  let bestId = null;
  let bestScore = 0;
  for (const [id, fn] of Object.entries(BIMANUAL_SCORERS)) {
    if (allowedIds && !allowedIds.has(id)) continue;
    const s = fn(hands);
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  if (bestScore < BIMANUAL_CONFIDENCE) return null;
  return { id: bestId, confidence: Math.round(bestScore * 100) / 100 };
}

export const BIMANUAL_THRESHOLDS = {
  BIMANUAL_CONFIDENCE,
  BOOK_WRIST_PROXIMITY_MAX,
  BOOK_HEIGHT_DIFF_MAX,
  MORE_PINCH_PROXIMITY_MAX,
  HOUSE_FINGERTIP_MEET_MAX,
  HOUSE_WRIST_SPREAD_MIN,
  HOUSE_WRIST_Y_MIN,
  HOUSE_TIP_Y_MAX,
};
